import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { daznService, backupService, AdminWvd, getErrorMessage } from '@/services';

/** Admin: the Widevine device file every customer uploaded, with a download button for each. */
export default function AdminWvds() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<AdminWvd[] | null>(null);
  const [downloading, setDownloading] = useState<number | null>(null);

  useEffect(() => {
    daznService.adminListWvds().then(setRows).catch((error) => {
      toast.error(getErrorMessage(error) || t('common.error'));
      setRows([]);
    });
  }, [t]);

  const download = async (row: AdminWvd) => {
    setDownloading(row.userId);
    try {
      const blob = await daznService.adminDownloadWvd(row.userId);
      backupService.downloadBlob(blob, `${row.username}-${row.originalName}`.replace(/[^A-Za-z0-9._-]/g, '_'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('wvd.admin.downloadError'));
    } finally {
      setDownloading(null);
    }
  };

  const answer = (r: AdminWvd) =>
    r.lastStatus === 'ok'
      ? { cls: 'badge-success', label: t('wvd.admin.answerOk') }
      : r.lastStatus === 'rejected'
        ? { cls: 'badge-error', label: t('wvd.admin.answerRejected', { code: r.lastStatusCode ?? '' }) }
        : { cls: 'badge-neutral', label: t('wvd.admin.answerNone') };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-dark-100">{t('wvd.admin.title')}</h1>
        <p className="text-dark-400 mt-1">{t('wvd.admin.subtitle')}</p>
      </div>

      <div className="card">
        {rows && rows.length === 0 ? (
          <p className="text-dark-400 text-sm">{t('wvd.admin.empty')}</p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('wvd.admin.user')}</th>
                  <th>{t('wvd.file')}</th>
                  <th>{t('wvd.systemId')}</th>
                  <th>{t('wvd.level')}</th>
                  <th>{t('wvd.uploadedOn')}</th>
                  <th>{t('wvd.admin.lastAnswer')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(rows ?? []).map((r) => {
                  const a = answer(r);
                  return (
                    <tr key={r.userId}>
                      <td>
                        <div className="text-dark-200 font-medium">{r.username}</div>
                        <div className="text-dark-500 text-xs">{r.email}</div>
                      </td>
                      <td className="break-all">{r.originalName}</td>
                      <td>
                        {r.systemId ?? '—'}
                        {r.sharedSystemId && <span className="badge-warning ml-2">{t('wvd.admin.sharedBadge')}</span>}
                      </td>
                      <td>{r.securityLevel ? `L${r.securityLevel}` : '—'}</td>
                      <td>{dayjs(r.uploadedAt).format('DD/MM/YYYY HH:mm')}</td>
                      <td><span className={a.cls}>{a.label}</span></td>
                      <td className="text-right">
                        <button type="button" className="btn-secondary inline-flex items-center gap-2" disabled={downloading === r.userId} onClick={() => void download(r)}>
                          <Download size={16} />
                          {t('wvd.admin.download')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
